import {
  IconActivity,
  IconEye,
  IconHeadset,
  IconHeart,
  IconShield,
  IconStar,
  IconTarget,
  IconUsers,
  ServiceIconBox,
} from '@/components/landing/LandingIcons';

const stats = [
  { value: '50K+', label: 'Happy Customers', icon: IconUsers, tone: 'orange' as const },
  { value: '1M+', label: 'Transactions', icon: IconActivity, tone: 'green' as const },
  { value: '99.9%', label: 'Uptime', icon: IconShield, tone: 'orange' as const },
  { value: '24/7', label: 'Support', icon: IconHeadset, tone: 'green' as const },
];

const coreValues = [
  {
    title: 'Customer First',
    desc: "Your satisfaction is our top priority. We're always here to serve you better.",
    icon: IconHeart,
    tone: 'orange' as const,
  },
  {
    title: 'Best Rates',
    desc: 'We offer the most competitive rates in the market without compromising quality.',
    icon: IconStar,
    tone: 'green' as const,
  },
  {
    title: 'Reliability',
    desc: 'Count on us for fast, secure, and seamless transactions every single time.',
    icon: IconShield,
    tone: 'orange' as const,
  },
  {
    title: 'Trust',
    desc: 'Built on transparency and integrity. Your trust is our greatest asset.',
    icon: IconEye,
    tone: 'green' as const,
  },
];

export function AboutSection() {
  return (
    <section id="about" className="py-20">
      <div className="container-page">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="section-title">About Us</h2>
          <p className="section-subtitle mx-auto">Your Trusted VTU Partner</p>
        </div>

        <div className="mx-auto mt-8 max-w-4xl space-y-4 text-center text-sm leading-relaxed text-brand-muted sm:text-base">
          <p>
            ASDATALINK is a leading Virtual Top-Up (VTU) platform based in Katsina, Nigeria. We provide seamless and affordable solutions for all your digital transactions including airtime, data bundles, cable TV subscriptions, utility bill payments, and educational pins.
          </p>
          <p>
            Founded with the vision to make digital transactions accessible to everyone, we have grown to become one of the most trusted platforms in Nigeria. Our commitment to excellence, security, and customer satisfaction sets us apart from the rest.
          </p>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat) => (
            <div key={stat.label} className="rounded-2xl border border-gray-100 bg-white p-6 text-center shadow-soft">
              <div className="mx-auto w-fit">
                <ServiceIconBox icon={stat.icon} tone={stat.tone} />
              </div>
              <p className="text-3xl font-extrabold text-brand-navy">{stat.value}</p>
              <p className="mt-1 text-sm text-brand-muted">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          <article className="rounded-2xl border border-gray-100 bg-brand-surface/50 p-8">
            <div className="inline-flex rounded-xl bg-primary/10 p-3 text-primary">
              <IconTarget className="h-6 w-6" />
            </div>
            <h3 className="mt-4 text-xl font-bold text-brand-navy">Our Mission</h3>
            <p className="mt-3 text-sm leading-relaxed text-brand-muted">
              To provide fast, reliable, and affordable digital services that empower individuals and businesses across Nigeria. We strive to simplify everyday transactions while delivering exceptional value and outstanding customer experience.
            </p>
          </article>
          <article className="rounded-2xl border border-gray-100 bg-brand-surface/50 p-8">
            <div className="inline-flex rounded-xl bg-secondary/10 p-3 text-secondary">
              <IconEye className="h-6 w-6" />
            </div>
            <h3 className="mt-4 text-xl font-bold text-brand-navy">Our Vision</h3>
            <p className="mt-3 text-sm leading-relaxed text-brand-muted">
              To become Africa&apos;s most trusted digital services platform, known for innovation, reliability, and customer-centric solutions. We envision a future where everyone has seamless access to essential digital services at their fingertips.
            </p>
          </article>
        </div>
      </div>
    </section>
  );
}

export function CoreValuesSection() {
  return (
    <section className="bg-brand-surface/40 py-20">
      <div className="container-page">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="section-title">Our Core Values</h2>
          <p className="section-subtitle mx-auto">
            The principles that guide everything we do at ASDATALINK
          </p>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {coreValues.map((value) => (
            <article key={value.title} className="rounded-2xl border border-gray-100 bg-white p-6 shadow-soft">
              <ServiceIconBox icon={value.icon} tone={value.tone} />
              <h3 className="font-bold text-brand-navy">{value.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-brand-muted">{value.desc}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export function AppPromoSection() {
  const features = [
    'Instant airtime & data purchase',
    'Bill payments on the go',
    'Transaction history & receipts',
    '24/7 customer support',
    'Secure & fast transactions',
    'Wallet funding via bank transfer',
  ];

  return (
    <section className="py-20">
      <div className="container-page grid items-center gap-10 lg:grid-cols-2">
        <div>
          <span className="inline-flex rounded-full bg-secondary/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-secondary">
            Mobile App
          </span>
          <h2 className="mt-4 section-title">Get Our Mobile App</h2>
          <p className="section-subtitle">
            Download the ASDATALINK mobile app and enjoy seamless transactions right from your smartphone. Available on Google Play Store.
          </p>

          <ul className="mt-6 space-y-3">
            {features.map((feature) => (
              <li key={feature} className="flex items-start gap-3 text-sm text-brand-muted">
                <span className="mt-0.5 inline-flex rounded-full bg-secondary/10 p-1 text-secondary">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="h-3.5 w-3.5">
                    <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                {feature}
              </li>
            ))}
          </ul>

          <a
            href="#"
            className="mt-8 inline-flex items-center gap-3 rounded-xl border border-gray-200 bg-brand-navy px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-navy/90"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6">
              <path d="M3.609 1.814 13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893 2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198 2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658 16.802 8.99l-2.303 2.303-8.635-8.635z" />
            </svg>
            Get it on Google Play
          </a>
        </div>

        <div className="relative">
          <span className="absolute -right-2 top-4 z-10 rounded-full bg-secondary px-4 py-1.5 text-sm font-bold text-white shadow-lg">
            Free!
          </span>
          <img
            src="/assets/dashboard-mockup.jpeg"
            alt="ASDATALINK App Dashboard"
            className="rounded-2xl border border-gray-100 shadow-card"
          />
          <p className="mt-3 text-center text-sm font-medium text-brand-muted">ASDATALINK App Dashboard</p>
        </div>
      </div>
    </section>
  );
}
